import React, { HTMLAttributes, Ref } from 'react';

import clsx from 'clsx';
import { useIntl } from 'react-intl';

import { FilterContainer } from '@atoms/filter-container';

import { Message, messageToString } from '@utils/message';

import { Text, Inline, Loader } from '@atoms';

import classes from './suggestion.module.scss';

type BaseSuggestionItem = {
  id: string;
  firstRow: string;
  secondRow: string;
};

type SuggestionProps<SI extends BaseSuggestionItem = BaseSuggestionItem> = {
  insideNav?: boolean;
  className?: string;
  suggestions: SI[];
  listClosed: boolean;
  loading: boolean;
  inputValue: string;
  inputRef: Ref<HTMLInputElement>;
  onInputChange: HTMLAttributes<HTMLInputElement>['onChange'];
  onInputFocus: HTMLAttributes<HTMLInputElement>['onFocus'];
  onInputKeydown: HTMLAttributes<HTMLInputElement>['onKeyDown'];
  onClear: () => void;
  onClickOutside: () => void;
  highlightedIndex: number;
  onSuggestionMouseEnter: (suggestion: SI) => void;
  onSuggestionClick: (suggestion: SI) => void;
  placeholder: Message;
};

export function Suggestion<SI extends BaseSuggestionItem = BaseSuggestionItem>({
  insideNav,
  className,
  listClosed,
  suggestions,
  loading,
  inputValue,
  inputRef,
  onInputChange,
  onInputFocus,
  onInputKeydown,
  onClear,
  onClickOutside,
  highlightedIndex,
  onSuggestionMouseEnter,
  onSuggestionClick,
  placeholder,
}: SuggestionProps<SI>) {
  const intl = useIntl();

  return (
    <FilterContainer
      className={clsx(
        classes.searchContainer,
        insideNav && classes.insideNav,
        insideNav && !listClosed && classes.insideNavFocused,
        className,
      )}
      prefixIcon="marker"
      noPadding
      dropdown={{
        fullWidth: true,
        component: (
          <div className={classes.searchSuggestions}>
            {suggestions.length === 0 && !loading && (
              <div className={classes.noResult}>
                <Inline
                  message={{ id: 'atlas.search.no_results' }}
                  color="black"
                />
              </div>
            )}
            {loading && (
              <div className={classes.loadingWrapper}>
                <Loader color="big_stone" loading />
              </div>
            )}
            {suggestions.map((suggestion, index) => {
              return (
                <div
                  key={suggestion.id}
                  onMouseEnter={() => onSuggestionMouseEnter(suggestion)}
                  onClick={() => onSuggestionClick(suggestion)}
                  className={clsx(
                    classes.suggestionItem,
                    highlightedIndex === index && classes.highlighted,
                  )}
                >
                  <Text margin={0} className={classes.firstRow}>
                    {suggestion.firstRow}
                  </Text>
                  <Text margin={0} className={classes.secondRow}>
                    {suggestion.secondRow}
                  </Text>
                </div>
              );
            })}
          </div>
        ),
        isOpen: !listClosed,
      }}
      onClear={onClear}
      onClickOutside={onClickOutside}
    >
      <input
        className={classes.searchInput}
        value={inputValue}
        placeholder={messageToString(placeholder, intl)}
        ref={inputRef}
        onChange={onInputChange}
        onFocus={onInputFocus}
        onKeyDown={onInputKeydown}
      />
    </FilterContainer>
  );
}
